// Phase 24A — Inventory Report page (with as-of-date reconstruction).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PresetsMenu from "@/components/reports/PresetsMenu";
import ExportButtons from "@/components/reports/ExportButtons";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import PageHeader from "@/components/PageHeader";

export default function InventoryReport() {
  const reportsBackTo = useReportsBackLink();
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    as_of: today, zone_id: "", sub_zone_id: "", distributor_id: "", sku_id: "",
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 100 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get("/reports/inventory", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const items = data?.items || [];
  const total = data?.total || 0;
  const isPast = filters.as_of && filters.as_of !== today;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="inventory-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: "Inventory" }]}
        title="Inventory report"
        subtitle="Distributor SKU stock movement over the reporting window."
        favorite={{ key: "reports.inventory-report", type: "screen", label: "Inventory report", route: "/reports/inventory" }}
        testid="inventoryreport-page-header"
      />

      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            Inventory Report
            {isPast && (
              <Badge variant="outline"
                       className="bg-amber-50 border-amber-200 text-amber-800"
                       data-testid="asof-past-badge">
                As-of past date
              </Badge>
            )}
          </h1>
          <p className="text-xs text-neutral-500">
            Distributor closing stock. Past-date values reconstructed by replaying stock movements.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PresetsMenu reportKey="inventory" filters={filters}
                          onApply={(f) => setFilters({ ...filters, ...f })} />
          <ExportButtons endpoint="/reports/inventory" params={filters} filename="inventory" />
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
                    data-testid="refresh-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                              showZone showSubZone showDistributor showAsOfDate />
        </CardContent>
      </Card>

      {data?.filter_summary && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> {data.filter_summary} · {total} rows
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs" data-testid="inventory-table">
              <thead className="bg-[#6D2C91] text-white sticky top-0">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className="text-left px-3 py-2 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={columns.length || 13}
                            className="text-center p-6 text-neutral-500">
                    No inventory rows for these filters.
                  </td></tr>
                ) : items.map((row, i) => (
                  <tr key={row.distributor_id + row.sku_id + i}
                        className="border-t hover:bg-neutral-50">
                    {columns.map(c => (
                      <td key={c.key} className="px-3 py-2 whitespace-nowrap">
                        {row[c.key] == null || row[c.key] === "" ? "—" : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > 100 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span>Page {page} of {Math.ceil(total / 100)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 100 >= total}
                      onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
